import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import EzTable from "../../../common/EzTable";
import { EzToast, TOAST_SEVERITY } from "../../../common/EzToast";
import EzNotFound from "../../../common/EzNotFound";
import EzDialog from "../../../common/EzDialog";
import { Box, Container, Grid } from "@mui/material";
import Breadcrumb from "../../../common/Breadcrumb";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { useStyles } from "./style";
import { printTableData } from "../../../utils/printUtils";
import EzControlledCalendarWithFloatingLabel from "../../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import EzControlledMultiSelecttWithFloatingLabel from "../../../common/EzForm/EzControlledMultiSelect/WithFloatingLabel";
import EzControlledInputWithFloatingLabel from "../../../common/EzForm/EzControlledInput/WithFloatingLabel";
import { getFormBaseValidationRule } from "../../../utils/form/form-utils";
import { formatDate, DATE_STRATEGY } from "../../../utils/date";

const getIconPath = (imgPathDelta = "/no-record.png") =>
  process.env.PUBLIC_URL + imgPathDelta;

const Affiliate = (props) => {
  const classes = useStyles();
  const [areFiltersVisible, toggleFilterState] = useState(true);
  const [isCopy, setIsCopy] = useState(false);
  const [isPrintError, setIsPrintError] = useState(false);
  const [isEmailDialogVisible, setIsEmailDialogVisible] = useState(false);
  const [selectedAffiliateForEmail, setSelectedAffiliateForEmail] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [emailInputError, setEmailInputError] = useState("");

  const {
    fetchAffiliates,
    tableData,
    setDeleteConfirmationPopupVisibility,
    exportAffiliate,
    emailAffiliateReport,
    isAffiliateExportError,
    isAffiliateExportSuccess,
    isEmailSending,
    isPageLevelError,
    paginationInfo,
    isLoading,
    screenPermissions,
    handleCreateAffiliate,
    editAffiliateRoute,
    savedTableState,
    onTableStateChange,
    locationDD,
    networkDD,
    fetchAffiliateLocationDD,
    fetchAffiliateNetworkDD,
    clearAffiliateEmailDropdowns,
    affiliateLocationDD,
    affiliateNetworkDD,
    isLoadingAffiliateDD,
  } = props;

  const {
    control: emailControl,
    formState: { errors: emailErrors },
    handleSubmit: handleEmailSubmit,
    reset: resetEmailForm,
  } = useForm({
    defaultValues: {
      startDate: null,
      endDate: null,
      locationId: [],
      networkId: [],
      recipientEmail: "",
    },
  });

  const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

  const validateEmail = (email) => {
    return emailRegex.test(email);
  };

  const handleChipAdd = (e) => {
    const newValue = e.value;
    
    if (newValue.length > recipientEmails.length) {
      const newEmail = newValue[newValue.length - 1];
      
      if (typeof newEmail === 'string') {
        const trimmedEmail = newEmail.trim();
        
        if (trimmedEmail.includes(',')) {
          const emails = trimmedEmail.split(',').map(email => email.trim()).filter(email => email !== '');
          const validEmails = [];
          let hasError = false;
          
          for (const email of emails) {
            if (validateEmail(email)) {
              const lowerEmail = email.toLowerCase();
              if (!recipientEmails.includes(lowerEmail) && !validEmails.includes(lowerEmail)) {
                validEmails.push(lowerEmail);
              }
            } else {
              setEmailInputError(`Invalid email address: ${email}`);
              hasError = true;
              break;
            }
          }
          
          if (!hasError && validEmails.length > 0) {
            setRecipientEmails([...recipientEmails, ...validEmails]);
            setEmailInputError("");
          }
          return;
        }
        
        if (!validateEmail(trimmedEmail)) {
          setEmailInputError(`Invalid email address: ${trimmedEmail}`);
          setRecipientEmails(recipientEmails);
          return;
        }
      
        const lowerEmail = trimmedEmail.toLowerCase();
        if (recipientEmails.includes(lowerEmail)) {
          setEmailInputError("Email already added");
          setRecipientEmails(recipientEmails);
          return;
        }
        setRecipientEmails([...recipientEmails, lowerEmail]);
        setEmailInputError("");
      }
    } else {
      setRecipientEmails(newValue);
      setEmailInputError("");
    }
  };

  const handleChipRemove = (e) => {
    setRecipientEmails(e.value);
    setEmailInputError("");
  };

  const breadcrumbD = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/affiliate", label: "Affiliate" },
  ];

  const convertToTSV = (data) => {
    const headers = data.columns.map((item) => item.colLabel).join("\t");
    const rows = data.rows
      .map((row) => {
        const { id, ...rowWithoutId } = row;
        return Object.values(rowWithoutId).join("\t");
      })
      .join("\n");
    return `${headers}\n${rows}`;
  };

  const copyToClipboard = () => {
    const csv = convertToTSV(tableData);
    navigator.clipboard
      .writeText(csv)
      .then(() => setIsCopy(true))
      .catch((err) => setIsCopy(true));
  };

  useEffect(() => {
    setTimeout(() => setIsCopy(false), 1000);
  }, [isCopy]);

  useEffect(() => {
    if (isPrintError) {
      setTimeout(() => setIsPrintError(false), 3000);
    }
  }, [isPrintError]);

  useEffect(() => {
    if (emailSuccess) {
      const timer = setTimeout(() => {
        setEmailSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [emailSuccess]);

  useEffect(() => {
    if (emailError) {
      const timer = setTimeout(() => {
        setEmailError(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [emailError]);

  const handlePrint = () => {
    const success = printTableData({
      title: "Affiliate",
      columns: tableData?.columns || [],
      rows: tableData?.rows || [],
      excludeColumns: ["id"],
      orientation: "landscape",
    });

    if (!success) {
      setIsPrintError(true);
    }
  };

  const handleListOfActiveAffiliates = () => {
    const activeStatusFilter = {
      filterField: "status",
      matchStrategy: "::",
      query: "Active",
    };
    
    fetchAffiliates({
      sortField: savedTableState?.sortField || "id",
      sortOrder: savedTableState?.sortOrder,
      pageNumber: 0,
      pageSize: savedTableState?.rows || 10,
      filters: [activeStatusFilter],
      userId: localStorage.getItem("userId"),
    });
  };
  const getEmailFormErrorMessage = (name) => {
    return (
      emailErrors[name] && <small className="p-error">{emailErrors[name].message}</small>
    );
  };

  const handleEmailRecord = (rowData) => {
    setSelectedAffiliateForEmail(rowData);
    
    const initialEmails = rowData.email ? [rowData.email.toLowerCase()] : [];
    setRecipientEmails(initialEmails);
    setEmailInputError("");
    
    resetEmailForm({
      startDate: null,
      endDate: null,
      locationId: [],
      networkId: [],
      recipientEmail: "",
    });
    
    if (rowData?.id) {
      fetchAffiliateLocationDD(rowData.id);
      fetchAffiliateNetworkDD(rowData.id);
    }
    setIsEmailDialogVisible(true);
  };

  const handleCloseEmailDialog = () => {
    setIsEmailDialogVisible(false);
    setSelectedAffiliateForEmail(null);
    setRecipientEmails([]);
    setEmailInputError("");
    resetEmailForm();
    clearAffiliateEmailDropdowns();
  };

  const onSendEmail = async (data) => {
    if (recipientEmails.length === 0) {
      setEmailInputError("At least one email is required");
      setEmailError(true);
      return;
    }

    const payload = {
      startDate: data.startDate ? formatDate(data.startDate, DATE_STRATEGY.DATE_ISO) : null,
      endDate: data.endDate ? formatDate(data.endDate, DATE_STRATEGY.DATE_ISO) : null,
      locationId: data.locationId || [],
      networkId: data.networkId || [],
      affiliateId: [selectedAffiliateForEmail?.id],
      recipientEmails: recipientEmails,
    };

    try {
      await emailAffiliateReport(payload);
      setEmailSuccess(true);
      handleCloseEmailDialog();
    } catch (error) {
      setEmailError(true);
    }
  };

  const renderPageHeaderSection = () => {
    return (
      <div className="make-relative sub-header">
        <div className="maintitlediv">
          <div className="ml-3">
            <h4 className="pagetitle">Affiliate</h4>
            <Button
              label={areFiltersVisible ? "Advanced Filter" : "Advanced Filter"}
              icon={areFiltersVisible ? "pi pi-filter-slash" : "pi pi-filter"}
              className={`p-button-text p-button-secondary shadow-btn`}
              onClick={() => {
                toggleFilterState(!areFiltersVisible);
              }}
            />
          </div>
          <div className="buttonarea">
            <div className="create-btn-group">
                <Button
                  label={"Filter Active Affiliates"}
                  className={` p-button-raised`}
                  style={{ borderRadius: "0px" }}
                  onClick={handleListOfActiveAffiliates}
                />
              {screenPermissions?.create && (
                <Button
                  label={"Create Affiliate"}
                  className={` p-button-raised`}
                  style={{ borderRadius: "0px" }}
                  onClick={handleCreateAffiliate}
                />
              )}
            </div>
            <div className="copy-excel-print-group">
              <Button
                label={"Copy"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={copyToClipboard}
              />
              <Button
                label={"Excel"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={exportAffiliate}
              />
              <Button
                label={"Print"}
                className={` p-button-raised`}
                style={{ borderRadius: "0px" }}
                onClick={handlePrint}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const shouldRenderFullPageError = () => isPageLevelError;
  const shouldRenderRecords = () => tableData?.columns?.length > 0;
  const shouldRenderNotFoundView = () =>
    !shouldRenderRecords() && !shouldRenderFullPageError();

  const getInitialFilters = () => {
    const defaultFilters = {
      global: { value: null },
      affiliateId: { value: null, filterKey: "affiliateId" },
      affiliateCode: { value: null, filterKey: "affiliateCode" },
      affiliateName: { value: null, filterKey: "affiliateName" },
      email: { value: null, filterKey: "email" },
      contactNumber: { value: null, filterKey: "contactNumber" },
      address: { value: null, filterKey: "address" },
      city: { value: null, filterKey: "city" },
      province: { value: null, filterKey: "province" },
      country: { value: null, filterKey: "country" },
      currencyCode: { value: null, filterKey: "currencyCode" },
      locations: { value: null, filterKey: "locations" },
      networks: { value: null, filterKey: "networks" },
      status: { value: null, filterKey: "status" },
      crtBy: { value: null, filterKey: "crtBy" },
      crtTime : { value: null, filterKey: "crtTime" },
      modTime: { value: null, filterKey: "modTime" },
    };

    if (savedTableState?.filters) {
      const savedFilters = savedTableState.filters;
      Object.keys(savedFilters).forEach((key) => {
        if (defaultFilters[key]) {
          defaultFilters[key] = { ...defaultFilters[key], ...savedFilters[key] };
        }
      });
    }

    return defaultFilters;
  };

  const filters = getInitialFilters();

  const getToastProps = () => {
    if (isCopy) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Data copied to clipboard!",
        shouldShowToast: true,
      };
    }

    if (isPrintError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Please allow popups to use the print feature.",
        shouldShowToast: true,
      };
    }

    if (emailSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Email sent successfully!",
        shouldShowToast: true,
      };
    }

    if (emailError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Failed to send email. Please try again.",
        shouldShowToast: true,
      };
    }

    return {};
  };

  const tableProps = {
    areFiltersVisible,
    columns: tableData?.columns,
    paginationInfo,
    screenPermissions,
    value: tableData?.rows,
    emptyMessage: "No Affiliate found.",
    filters,
    loadLazyData: fetchAffiliates,
    loading: isLoading,
    showMoreActions: false,
    sortField: savedTableState?.sortField || "id",
    initialSortOrder: savedTableState?.sortOrder,
    initialFirst: savedTableState?.first || 0,
    initialRows: savedTableState?.rows || 10,
    onTableStateChange,
    setDeleteConfirmationPopupVisibility,
    onEditRecord: editAffiliateRoute,
    showViewButton: false,
    showCopyButton: false,
    showDeleteButton: false,
    showEditButton: true,
    showEmailButton: true,
    onEmailRecord: handleEmailRecord,
  };

  return (
    <>
      <Container maxWidth="lg">
        <Breadcrumb list={breadcrumbD} />
        <EzToast {...getToastProps()} />
        <div className={`containerbox ${classes.bg}`}>
          {renderPageHeaderSection()}
          <div className="mTop15">
            {shouldRenderFullPageError() && (
              <EzNotFound
                messageStyle={{ marginLeft: "-70px" }}
                bgImgUrl={getIconPath("something-broke.png")}
                message={"Oops! Something broke, we are working on a fix."}
              />
            )}
            {shouldRenderRecords() && (
              <Box>
                <EzTable {...tableProps} />
              </Box>
            )}
            {shouldRenderNotFoundView() && (
              <EzNotFound
                bgImgUrl={getIconPath()}
                message={"No Affiliate records to show"}
              />
            )}
          </div>
        </div>
      </Container>

      <EzDialog
        header="Email Affiliate Report"
        visible={isEmailDialogVisible}
        onHide={handleCloseEmailDialog}
        width="600px"
        height="310px"
        maximizable={false}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={handleCloseEmailDialog}
              disabled={isEmailSending}
            />
            <Button
              label="Send Email"
              icon="pi pi-send"
              className="p-button-raised"
              onClick={handleEmailSubmit(onSendEmail)}
              disabled={isEmailSending}
              loading={isEmailSending}
            />
          </div>
        }
      >
        <div className="form-demo" style={{ paddingBottom: 0 }}>
          <form className="p-fluid">
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <EzControlledCalendarWithFloatingLabel
                  name="startDate"
                  control={emailControl}
                  label="Start Date"
                  rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  isError={emailErrors.startDate}
                  errorMsg={getEmailFormErrorMessage("startDate")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EzControlledCalendarWithFloatingLabel
                  name="endDate"
                  control={emailControl}
                  label="End Date"
                  rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                  isError={emailErrors.endDate}
                  errorMsg={getEmailFormErrorMessage("endDate")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EzControlledMultiSelecttWithFloatingLabel
                  name="locationId"
                  control={emailControl}
                  rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                  optionLabel={"name"}
                  optionValue={"id"}
                  options={affiliateLocationDD || []}
                  isError={emailErrors["locationId"]}
                  errorMsg={getEmailFormErrorMessage("locationId")}
                  label="Location"
                  labelClassName={"ml-2"}
                  loading={isLoadingAffiliateDD}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EzControlledMultiSelecttWithFloatingLabel
                  name="networkId"
                  control={emailControl}
                  rules={getFormBaseValidationRule().DEFAULT.DO_NOT_VALIDATE}
                  optionLabel={"name"}
                  optionValue={"id"}
                  options={affiliateNetworkDD || []}
                  isError={emailErrors["networkId"]}
                  errorMsg={getEmailFormErrorMessage("networkId")}
                  label="Network"
                  labelClassName={"ml-2"}
                  loading={isLoadingAffiliateDD}
                />
              </Grid>
              <Grid item xs={12}>
                <div className="field">
                  <label htmlFor="recipientEmails" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Recipient Email <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    {recipientEmails.length === 0 && (
                      <div 
                        style={{ 
                          position: 'absolute',
                          top: '50%',
                          left: '0.75rem',
                          transform: 'translateY(-50%)',
                          color: '#6c757d',
                          pointerEvents: 'none',
                          zIndex: 1,
                          fontSize: '0.875rem'
                        }}
                      >
                      </div>
                    )}
                    <Chips
                      id="recipientEmails"
                      value={recipientEmails}
                      onChange={handleChipAdd}
                      onRemove={handleChipRemove}
                      separator=","
                      className={emailInputError ? 'p-invalid' : ''}
                      style={{ width: '100%' }}
                      disabled={isEmailSending}
                    />
                  </div>
                  {emailInputError && (
                    <small className="p-error" style={{ display: 'block', marginTop: '0.25rem' }}>
                      {emailInputError}
                    </small>
                  )}
                  {recipientEmails.length === 0 && !emailInputError && (
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#6c757d' }}>
                      Add emails by typing and pressing Enter, or paste comma-separated emails
                    </small>
                  )}
                </div>
              </Grid>
            </Grid>
          </form>
        </div>
      </EzDialog>
    </>
  );
};

export default Affiliate;
